import type { Decision, EspaceOverview } from "@shared/espace.types";
import { useCallback, useEffect, useState } from "react";
import { fetchJson, HttpError, messageDeSecours } from "@/lib/http";

/**
 * L'espace tient dans un seul appel, et donc dans un seul état.
 *
 * Chaque écran lit la même vue plutôt que d'aller chercher sa part : à ce volume
 * (quelques dizaines d'offres, quelques centaines de profils), quatre appels
 * feraient quatre chargements visibles pour rien.
 *
 * Les quatre appels passent par `fetchJson`. Écrits à la main, ils n'avaient aucun
 * délai : un serveur qui n'honorait jamais la requête laissait l'écran en
 * chargement pour toujours, sans erreur, sans bouton, rien d'autre à faire que
 * recharger la page.
 */

type State =
	| { status: "loading" }
	| { status: "anonymous" }
	/**
	 * `message` porte le texte à montrer. L'état ne disait rien de plus que
	 * « raté », donc l'écran ne pouvait proposer que « réessayez » : une connexion
	 * coupée, un serveur en carafe et un délai dépassé n'appellent pas le même
	 * geste, et c'est `messageDeSecours` qui fait la différence.
	 */
	| { status: "failed"; message: string }
	| { status: "ready"; overview: EspaceOverview };

export function useEspace() {
	const [state, setState] = useState<State>({ status: "loading" });

	const load = useCallback(async () => {
		try {
			const overview = await fetchJson<EspaceOverview>("/api/espace");
			setState({ status: "ready", overview });
		} catch (error) {
			// Un 401 n'est pas une panne : c'est une session finie. L'écran renvoie
			// vers la saisie du code, il n'affiche pas un message d'erreur.
			if (error instanceof HttpError && error.status === 401) {
				setState({ status: "anonymous" });
				return;
			}
			setState({ status: "failed", message: messageDeSecours(error) });
		}
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	/**
	 * La réponse est écrite d'abord dans l'écran, puis envoyée.
	 *
	 * Attendre le serveur pour recolorer un bouton donnerait une interface molle
	 * sur douze profils d'affilée. En cas d'échec on RECHARGE : afficher une
	 * réponse qui n'est pas enregistrée serait pire que le délai qu'on évite.
	 */
	const answer = useCallback(
		async (profileId: string, decision: Decision | null, comment: string | null) => {
			setState((current) => {
				if (current.status !== "ready") return current;
				return {
					status: "ready",
					overview: {
						...current.overview,
						selections: current.overview.selections.map((selection) => ({
							...selection,
							profiles: selection.profiles.map((profile) =>
								profile.id === profileId
									? { ...profile, decision, comment, decidedAt: decision ? "maintenant" : null }
									: profile,
							),
						})),
					},
				};
			});

			try {
				await fetchJson(`/api/espace/profils/${profileId}/reponse`, {
					method: "POST",
					body: { decision, comment },
				});
			} catch {
				// Le rechargement rattrape aussi la session finie : le 401 arrive alors
				// sur `load`, qui le lit comme tel et bascule en `anonymous`.
				await load();
			}
		},
		[load],
	);

	/**
	 * L'écriture de la fiche. Ici l'ordre est INVERSE de la réponse aux profils :
	 * le serveur d'abord, l'écran ensuite.
	 *
	 * Une réponse à un profil est un clic dans une liste, et l'attendre rendrait la
	 * page molle sur douze profils. Un champ de formulaire est un geste unique et
	 * lent : afficher « Enregistré » avant que le serveur ait accepté serait mentir
	 * précisément là où l'entreprise vérifie qu'on l'a bien pris.
	 */
	const saveCompany = useCallback(async (patch: Record<string, unknown>): Promise<boolean> => {
		try {
			await fetchJson("/api/espace/entreprise", { method: "PATCH", body: patch });
		} catch {
			return false;
		}

		setState((current) => {
			if (current.status !== "ready") return current;
			const { fullName, phone, ...company } = patch;
			return {
				status: "ready",
				overview: {
					...current.overview,
					contact: {
						...current.overview.contact,
						...(fullName !== undefined ? { fullName: (fullName as string) || null } : {}),
						...(phone !== undefined ? { phone: (phone as string) || null } : {}),
					},
					company: current.overview.company
						? { ...current.overview.company, ...company }
						: current.overview.company,
				},
			};
		});
		return true;
	}, []);

	/**
	 * Redéposer une offre. On RECHARGE au lieu d'insérer la copie dans l'état :
	 * c'est le serveur qui décide de son identifiant et de sa date, et deviner ces
	 * deux-là ferait diverger l'écran de la base au premier détail.
	 */
	const redeposer = useCallback(
		async (requestId: string): Promise<string | null> => {
			const path = `/api/espace/offres/${requestId}/redeposer`;
			try {
				const body = await fetchJson<{ id: string }>(path, { method: "POST" });
				await load();
				return body.id;
			} catch {
				// ⚠️ On recharge AUSSI quand ça rate. Un délai dépassé abandonne côté
				// navigateur, il n'annule rien côté serveur : la copie peut très bien
				// exister. L'écran appelant invite alors à recommencer, et l'entreprise se
				// retrouve avec deux offres identiques. Après le rechargement, elle voit
				// la copie si elle a été créée.
				await load();
				return null;
			}
		},
		[load],
	);

	return { state, reload: load, answer, saveCompany, redeposer };
}
